/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { klona } from 'klona';
import { Device } from '@capacitor/device';
import { App } from '@capacitor/app';
import { useDocumentVisibility, useGetState, useInterval, useUnmountedRef } from 'ahooks';
import { Icon } from '@fuxi/eevee-icon';
import { Loading3QuartersOutlined } from '@ant-design/icons';
import { Dialog } from 'antd-mobile';
import { useDispatch, useSelector } from 'react-redux';
import { DeviceStatePhoto, MachineState, storagePrefixV2, ALL_PAGES, TypeImage } from '@/constant';
import service from '@/service';
import { Toast, getPadVersion, isNewSoftwareVersion } from '@/utils';
import { Header, CheckArm, VoiceDetection, When } from '@/components';
import { CheckArmRefAttrs } from '@/components/check-arm';
import { VoiceDetectionRefAttrs } from '@/components/voice-detection';
import { useCheckLocationPermission } from '@/hooks';
import { selectedApkVersion, selectedIsCanNavToMassageControl } from '@/store/device';
import Carousel from '@/components/carousel';
import { selectCurrentServingOrder, setCurrentServingOrder } from '@/store/service';
import useNav from '../../../hooks/useNav';
import cx from './index.module.scss';

interface Props {}

export const LOCK_TIME = 8000;
// 两次点击最长间隔时间
const LARGEST_INTERVAL_TIME = 1500;
const CLICK_OPEN_VOICE_DETECT_TIMES = 5;

interface CurrentClickInfo {
  lastClickTime: number;
  deviceId: string;
  clickedTimes: number;
}

const calculationRemainTime = (seconds: number) => {
  if (seconds > 60) {
    // 120-61秒区间
    return '2分钟';
  } else if (seconds > 30) {
    // 60-31秒区间
    return '1分钟';
  } else if (seconds > 10) {
    // 30-11秒区间
    return '30秒';
  } else {
    // 10-0秒区间
    return '10秒';
  }
};

/** 是否展示机器锁机文案和状态 */
export const isShowMachineLockState = (deviceInfo: DeviceItem) =>
  deviceInfo?.controlState === 'lock' && ['ManualOnly', 'Abnormal', 'Idle'].includes(deviceInfo?.state);

const DeviceManagement: React.FC<Props> = () => {
  const nav = useNav();
  const isUnMount = useUnmountedRef();
  const isCanNavToMassageControl = useSelector(selectedIsCanNavToMassageControl);
  const currentServingOrder: GetServiceOrderListResponse = useSelector(selectCurrentServingOrder);
  const dispatch = useDispatch();
  const apkVersion = useSelector(selectedApkVersion);
  const [deviceListOriginal, setDeviceListOriginal] = useState<DeviceItem[]>([]);
  const [deviceList, setDeviceList] = useState<DeviceItem[]>([]);
  const [deviceCardList, setDeviceCardList] = useState<DeviceItem[][]>(null);
  const [loadingConfig, setLoadingConfig, getLoadingConfig] = useGetState<{
    [index: number]: { lock?: boolean; unlock?: boolean };
  }>({});
  const voiceDetectionRef = useRef<VoiceDetectionRefAttrs>(null);
  const [isShuttingDownDevice, setIsShuttingDownDevice] = useState<DeviceItem>(null);

  // 用于判断点击停驻或者解锁后，点击其他页面，再回到当前页面，如果设备正在解锁或者停驻状态，弹出设备解锁或者成功弹窗
  const [deviceState, setDeviceState, getDeviceState] = useGetState<{
    [deviceId: string]: { lock?: boolean; unlock?: boolean };
  }>({});
  const isClickOptionsSkipPage = useRef<boolean>(true);
  const domVisibility = useDocumentVisibility();

  const checkArmRef = useRef<CheckArmRefAttrs>(null);
  const currentClickDeviceItem = useRef<CurrentClickInfo>({} as any);

  useInterval(
    () => {
      getDeviceList();
    },
    2000,
    { immediate: true }
  );

  const { checkLocationPermission } = useCheckLocationPermission();

  const getDeviceList = useCallback(async () => {
    if (domVisibility !== 'visible') return;

    const { data } = await service.device.getDeviceList();
    setDeviceListOriginal(data);

    const deviceInfo = await Device.getInfo();
    let appVersion = getPadVersion(apkVersion, deviceInfo?.platform);
    if (deviceInfo.platform === 'android') {
      const appInfo = await App.getInfo();
      appVersion = appInfo?.version;
    }

    // 确保多次关机只弹出一次弹窗
    if (!isShuttingDownDevice?.deviceId) {
      const isShuttingDownDevice = data?.find(item => item.state === 'Offline' && item.powerOffHint);
      setIsShuttingDownDevice(isShuttingDownDevice);
    }

    // 根据机器状态排序
    const allStateMachine: Map<DeviceItem['state'], DeviceItem[]> = new Map();

    data
      ?.filter(item => !isShowMachineLockState(item))
      .forEach(item => {
        // 判断是否为「本Pad不可操作」（NotUse）
        const isNotUse = isNewSoftwareVersion(appVersion, '2.0.0')
          ? !isNewSoftwareVersion(item?.softwareVersion, '2.0.0')
          : isNewSoftwareVersion(item?.softwareVersion, '2.0.0');

        if (isNotUse) {
          const notUseItem = { ...item, state: 'NotUse' } as DeviceItem;
          allStateMachine.set('NotUse', [...(allStateMachine.get('NotUse') || []), notUseItem]);
        } else {
          allStateMachine.set(item.state, [...(allStateMachine.get(item.state) || []), item]);
        }
      });

    const lockedMachineList = data?.filter(item => isShowMachineLockState(item));

    const sortedMachine: DeviceItem[] = [
      ...(allStateMachine.get('ShuttingDown') || []),
      ...(allStateMachine.get('Idle') || []),
      ...(allStateMachine.get('ManualOnly') || []),
      // 排序未知
      ...(allStateMachine.get('Upgrading') || []),
      ...(allStateMachine.get('UpgradeSuccess') || []),
      ...(allStateMachine.get('UpgradeFail') || []),
      ...(allStateMachine.get('Busy') || []),
      ...(allStateMachine.get('BeyondRange') || []),
      ...(allStateMachine.get('Abnormal') || []),
      ...(lockedMachineList || []),
      ...(allStateMachine.get('NotUse') || []),
      ...(allStateMachine.get('Offline') || []),
    ];

    const deviceList = [];
    for (let i = 0; i < sortedMachine.length; i += 6) {
      deviceList.push(sortedMachine.slice(i, i + 6));
    }

    setDeviceList(sortedMachine);
    setDeviceCardList(deviceList);
  }, [domVisibility, isShuttingDownDevice?.deviceId]);

  const handleParking = useCallback(
    (index: number, item: DeviceItem) => async e => {
      e?.stopPropagation();
      if (!item?.deviceId) return;

      if (
        !['Idle', 'ManualOnly'].includes(item.state) ||
        loadingConfig[index]?.lock ||
        loadingConfig[index]?.unlock ||
        item.chassisState !== 'Static'
      ) {
        return;
      }
      isClickOptionsSkipPage.current = false;
      let timer = null;

      const handleLockSuccess = () => {
        if (isUnMount.current) {
          return;
        }
        Toast.success('停驻成功');
        getLoadingConfig()[index] = { ...getLoadingConfig()[index], lock: false };
        setLoadingConfig(klona(getLoadingConfig()));
      };

      loadingConfig[index] = { ...loadingConfig[index], lock: true };
      setLoadingConfig(klona(loadingConfig));
      try {
        await service.lanInterface.parking(item.deviceId);

        handleLockSuccess();
      } catch {
        clearTimeout(timer);
        loadingConfig[index] = { ...loadingConfig[index], lock: false };
        setLoadingConfig(klona(loadingConfig));
      }
    },
    [getLoadingConfig, isUnMount, loadingConfig, setLoadingConfig]
  );

  // 底盘停驻解锁
  const handleCancelParking = useCallback(
    (index: number, item: DeviceItem) => async e => {
      e?.stopPropagation();
      if (!item?.deviceId) return;

      if (
        !['Idle', 'ManualOnly'].includes(item.state) ||
        loadingConfig[index]?.lock ||
        loadingConfig[index]?.unlock ||
        item.chassisState !== 'Static'
      ) {
        return;
      }
      isClickOptionsSkipPage.current = false;
      let timer = null;

      const handleUnlockSuccess = () => {
        if (isUnMount.current) {
          return;
        }
        Toast.success('解锁成功');
        getLoadingConfig()[index] = { ...getLoadingConfig()[index], unlock: false };
        setLoadingConfig(klona(getLoadingConfig()));
      };

      loadingConfig[index] = { ...loadingConfig[index], unlock: true };
      setLoadingConfig(klona(loadingConfig));
      try {
        await service.lanInterface.cancelParking(item.deviceId);
        handleUnlockSuccess();
      } catch {
        clearTimeout(timer);
        loadingConfig[index] = { ...loadingConfig[index], unlock: false };
        setLoadingConfig(klona(loadingConfig));
      }
    },
    [getLoadingConfig, isUnMount, loadingConfig, setLoadingConfig]
  );

  const handleDeviceItemNotUse = useCallback(
    (deviceItem: DeviceItem) => () => {
      // 判断设备原始状态
      const deviceState = deviceListOriginal?.find(itm => itm.deviceId === deviceItem?.deviceId)?.state;
      if (['Idle']?.includes(deviceState)) {
        Toast.show('本 Pad 不可操作', { duration: 2000 });
      }
      return (e?: React.MouseEvent) => {
        e?.stopPropagation(); // 确保事件不继续冒泡
      };
    },
    [deviceListOriginal]
  );

  // 定位辅助
  const handlePosAssist = useCallback(
    (deviceInfo: DeviceItem, index: number) => async e => {
      if (deviceInfo?.state === 'NotUse') {
        handleDeviceItemNotUse(deviceInfo);
        return;
      }
      e?.stopPropagation();
      if (!isCanNavToMassageControl) return;
      isClickOptionsSkipPage.current = false;

      if (deviceInfo.state !== 'ManualOnly' && deviceInfo?.chassisState !== 'Static') return;

      if (deviceInfo?.state !== 'Idle' || loadingConfig[index]?.lock || loadingConfig[index]?.unlock) return;

      try {
        await checkLocationPermission();
        console.log('定位权限校验通过');
      } catch (e) {
        console.log('定位权限校验失败', e);
        Toast.fail('请打开Pad的定位权限');
        return;
      }

      try {
        await checkArmRef.current?.handleCheckArm(deviceInfo);
      } catch (e) {
        console.error(e);
      }
    },
    [checkLocationPermission, handleDeviceItemNotUse, isCanNavToMassageControl, loadingConfig]
  );

  const handleShutdown = useCallback(
    (deviceInfo: DeviceItem, index: number) => async e => {
      e?.stopPropagation();
      if (!deviceInfo?.deviceId) return;

      if (!['Idle', 'ManualOnly']?.includes(deviceInfo?.state)) return;

      Dialog.confirm({
        title: '确认关闭设备吗？',
        content: `确认关闭${deviceInfo?.deviceName}设备`,
        confirmText: '确认',
        onConfirm: async () => {
          try {
            await service.device.shutDownDevice(deviceInfo?.deviceId);
          } catch (e) {
            console.error(e);
          }
        },
      });
    },
    []
  );

  const currentDevice = useMemo(
    () => deviceList?.find(item => item.deviceId === currentServingOrder?.device?.deviceId),
    [currentServingOrder, deviceList]
  );

  const currentOrderMachineIndex = useMemo(
    () => deviceList?.findIndex(item => item.deviceId === currentServingOrder?.device?.deviceId),
    [currentServingOrder?.device?.deviceId, deviceList]
  );

  const currentDeviceIsDisabled = useMemo(
    () =>
      !!(
        !['Idle', 'ManualOnly'].includes(currentDevice?.state) ||
        loadingConfig[currentOrderMachineIndex]?.lock ||
        loadingConfig[currentOrderMachineIndex]?.unlock ||
        deviceState[currentServingOrder?.device?.deviceId]?.lock ||
        deviceState[currentServingOrder?.device?.deviceId]?.unlock
      ),
    [currentDevice, loadingConfig, currentOrderMachineIndex, deviceState, currentServingOrder]
  );

  const deviceBgStyle = useMemo(
    () => ({
      width: '100vw',
      left: '-124px',
      top: 0,
      backgroundImage: `url(${storagePrefixV2}${
        TypeImage[
          currentServingOrder?.modelConfig?.[0]?.value?.includes('matrix')
            ? 'MatrixMoxibustion'
            : currentServingOrder?.modelConfig?.[0]?.key
        ]
      })`,
    }),
    [currentServingOrder]
  );

  const handleSetMicState = useCallback(async (item: DeviceItem) => {
    if (!item?.deviceId) return;
    if (!['Idle', 'ManualOnly'].includes(item.state) || item?.chassisState !== 'Static') {
      return;
    }

    try {
      await service.lanInterface.setMicState(item.deviceId, { enable: !item.microphoneState });
    } catch (e) {
      console.error(e);
    }
  }, []);

  // 连点五次进入机器人语音检测模块
  const handleClickDeviceItem = useCallback(
    (deviceItem: DeviceItem) => async () => {
      if (deviceItem?.controlState === 'lock') {
        Dialog.clear();
        Dialog.alert({
          title: '设备已锁机',
          content: (
            <div>
              <div>该设备已被代理商远程锁机，</div>
              <div>请联系代理商恢复使用</div>
              {!!deviceItem?.contactInfo && (
                <div style={{ color: '#0055FF' }}>
                  联系方式：
                  <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{deviceItem?.contactInfo}</span>
                </div>
              )}
            </div>
          ),
          confirmText: '我知道了',
        });
        return;
      }

      if (!isNewSoftwareVersion(deviceItem?.softwareVersion, '1.7.10')) return;
      if (!['Idle', 'ManualOnly'].includes(deviceItem.state)) return;

      if (currentClickDeviceItem.current.deviceId) {
        // 判断当前所点击的设备ID与之前点击的ID是否一致，且两次点击间隔时间须小于 LARGEST_INTERVAL_TIME
        if (
          currentClickDeviceItem.current.deviceId === deviceItem.deviceId &&
          Date.now() - currentClickDeviceItem.current.lastClickTime < LARGEST_INTERVAL_TIME
        ) {
          currentClickDeviceItem.current = {
            clickedTimes: currentClickDeviceItem.current.clickedTimes + 1,
            lastClickTime: Date.now(),
            deviceId: deviceItem.deviceId,
          };
        } else {
          console.log('id不一致或两次点击超过最大时长，重置计数器');
          // 设备ID不一致或者超出了最长间隔时间，则重置
          currentClickDeviceItem.current = {
            clickedTimes: 1,
            lastClickTime: Date.now(),
            deviceId: deviceItem.deviceId,
          };
        }
      } else {
        currentClickDeviceItem.current = {
          clickedTimes: 1,
          lastClickTime: Date.now(),
          deviceId: deviceItem.deviceId,
        };
      }

      if (currentClickDeviceItem.current.clickedTimes === CLICK_OPEN_VOICE_DETECT_TIMES) {
        const { data } = await service.device.getDeviceList();

        const currentDevice = data?.find(item => item.deviceId === deviceItem.deviceId);

        if (currentDevice?.controlState === 'lock') {
          Dialog.alert({
            title: '设备已锁机',
            content: (
              <div>
                <div>该设备已被代理商远程锁机，</div>
                <div>请联系代理商恢复使用</div>
                {!!currentDevice?.contactInfo && (
                  <div style={{ color: '#0055FF' }}>
                    联系方式：
                    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{currentDevice?.contactInfo}</span>
                  </div>
                )}
              </div>
            ),
            confirmText: '我知道了',
            bodyClassName: cx('software-not-support'),
          });
        } else {
          voiceDetectionRef.current.handleDetection(deviceItem);
        }
        currentClickDeviceItem.current = {} as any;
      }
    },
    []
  );

  // eslint-disable-next-line fuxi/require-useEffect-before-return
  useEffect(() => {
    if (isClickOptionsSkipPage.current) {
      // 初次载入页面 判断是否有机器停驻or解锁中
      deviceList?.forEach(deviceItem => {
        if (deviceItem.chassisState === 'Locking') {
          setDeviceState(prevValue => {
            const backupValue = klona(prevValue);
            backupValue[deviceItem.deviceId] = { ...backupValue[deviceItem.deviceId], lock: true };
            return backupValue;
          });
        } else if (deviceItem.chassisState === 'Unlocking') {
          setDeviceState(prevValue => {
            const backupValue = klona(prevValue);
            backupValue[deviceItem.deviceId] = { ...backupValue[deviceItem.deviceId], unlock: true };
            return backupValue;
          });
        } else if (deviceItem.chassisState === 'Static') {
          if (getDeviceState()[deviceItem.deviceId]?.lock) {
            getDeviceState()[deviceItem.deviceId] = { ...getDeviceState()[deviceItem.deviceId], lock: false };
            setDeviceState(klona(getDeviceState()));
            Toast.success('停驻成功');
          } else if (getDeviceState()[deviceItem.deviceId]?.unlock) {
            getDeviceState()[deviceItem.deviceId] = { ...getDeviceState()[deviceItem.deviceId], unlock: false };
            setDeviceState(klona(getDeviceState()));
            Toast.success('解锁成功');
          }
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceList]);

  const handleUpgrade = useCallback(
    (deviceItem: DeviceItem, e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e?.stopPropagation();

      Dialog.confirm({
        title: (
          <>
            <span>是否确认升级至</span>
            <span className={cx('dialog-title-version')}>V{deviceItem?.upgradeSoftwareVersion}</span>
            <span>版本</span>
          </>
        ),
        content: (
          <div className={cx('upgrade-modal-content')}>
            <div>待升级机器：{deviceItem?.deviceName}</div>
            <div>当前机器版本号：V{deviceItem?.softwareVersion}</div>
            <div>升级过程中机器将重启</div>
          </div>
        ),
        cancelText: '暂不升级',
        confirmText: '确认升级',
        onConfirm: async () => {
          try {
            await service.device.upgradeVersion(deviceItem.deviceId);
            getDeviceList();
          } catch (e) {
            console.log(e);
          }
        },
      });
    },
    [getDeviceList]
  );

  const handleIssueDetect = useCallback(
    (deviceItem: DeviceItem) => () => {
      if (deviceItem?.state === 'NotUse') return;
      Dialog.clear();
      Dialog.confirm({
        title: '确认进行误差排查？',
        content: (
          <div style={{ paddingBottom: '12px' }}>
            <div>请确认在触控笔有电的情况下执行，</div>
            <div>排查时间预计6分钟左右。</div>
          </div>
        ),
        confirmText: '确认',
        onConfirm: async () => {
          const { data } = await service.device.getDeviceInfoById(deviceItem?.deviceId);

          if (!data?.troubleShootToolsEnabled) {
            Toast.fail('该机器误差排查权限已关闭，请联系售后。');
          } else {
            nav(ALL_PAGES.ISSUE_DETECT_TOOL, {
              state: {
                deviceId: deviceItem?.deviceId,
              },
            });
          }
        },
      });
    },
    [nav]
  );

  /** 当机器状态更改时，更新订单状态 */
  const updateOrderState = useCallback(async () => {
    const { data } = await service.service.getServingOrderByOperator();
    dispatch(setCurrentServingOrder(data));
  }, [dispatch]);

  useEffect(() => {
    updateOrderState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDevice?.state]);

  useEffect(() => {
    if (!!isShuttingDownDevice?.deviceId) {
      Dialog.alert({
        title: '温馨提示',
        content: (
          <div>
            <div>{isShuttingDownDevice?.deviceName}主机已关闭</div>
            <div>请关闭设备电源</div>
          </div>
        ),
        confirmText: '我知道了',
        onConfirm: async () => {
          // 服务端事件
          try {
            await service.device.hintShutDown(isShuttingDownDevice?.deviceId);
          } catch (e) {
          } finally {
            //清空缓存关机的设备
            setIsShuttingDownDevice(null);
          }
        },
      });
    }
  }, [isShuttingDownDevice?.deviceId, isShuttingDownDevice?.deviceName]);

  return (
    <div
      className={cx('device-management', { 'device-running': !!currentServingOrder?.deviceId })}
      style={currentServingOrder?.deviceId && deviceBgStyle}
    >
      <div style={{ marginRight: '32px' }}>
        <Header showBack={false} showBorder={false} title="当前设备" />
      </div>
      <CheckArm ref={checkArmRef} onCheckArmPass={deviceInfo => nav(ALL_PAGES.POS_ASSIST, { state: deviceInfo })} />
      <VoiceDetection ref={voiceDetectionRef} />
      {currentServingOrder?.customerId ? (
        <div className={cx('device-running-wrapper')}>
          <div className={cx('device-running-card', { disabled: currentDeviceIsDisabled })}>
            <div className={cx('running-info')}>
              <div className={cx('device-version')}>V{currentDevice?.softwareVersion}</div>
              <div className={cx('info-name')}>{currentDevice?.deviceName}</div>
              <div className={cx('info-id')}>{currentDevice?.deviceId}</div>
              <div className={cx('info-state')}>服务中</div>
            </div>
            <div className={cx('running-option')}>
              <div
                className={cx('control-item', 'control-item-none')}
                onClick={handleParking(currentOrderMachineIndex, currentDevice)}
              >
                <button className={cx('item-btn')}>
                  {loadingConfig[currentOrderMachineIndex]?.lock ||
                  deviceState[currentServingOrder?.device?.deviceId]?.lock ? (
                    <Loading3QuartersOutlined spin />
                  ) : (
                    <Icon name="lock" className={cx('icon')} />
                  )}
                </button>
                <div>
                  停驻
                  {loadingConfig[currentOrderMachineIndex]?.lock ||
                  deviceState[currentServingOrder?.device?.deviceId]?.lock
                    ? '中'
                    : ''}
                </div>
              </div>
              <div
                className={cx('control-item', 'control-item-none')}
                onClick={handleCancelParking(currentOrderMachineIndex, currentDevice)}
              >
                <button className={cx('item-btn')}>
                  {loadingConfig[currentOrderMachineIndex]?.unlock ||
                  deviceState[currentServingOrder?.device?.deviceId]?.unlock ? (
                    <Loading3QuartersOutlined spin />
                  ) : (
                    <Icon name="unlock" className={cx('icon')} />
                  )}
                </button>
                <div>
                  解锁
                  {loadingConfig[currentOrderMachineIndex]?.unlock ||
                  deviceState[currentServingOrder?.device?.deviceId]?.unlock
                    ? '中'
                    : ''}
                </div>
              </div>
              {isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.6.5') && (
                <div
                  className={cx('control-item', { 'is-hidden': currentServingOrder.step === 'RoboticTask' })}
                  onClick={() => handleSetMicState(currentDevice)}
                >
                  <button className={cx('item-btn')}>
                    <Icon name={currentDevice?.microphoneState ? 'mic-close' : 'microphone'} className={cx('icon')} />
                  </button>
                  <div>{currentDevice?.microphoneState ? '闭麦' : '开麦'}</div>
                </div>
              )}
              <div
                className={cx('control-item', 'auxiliary-position')}
                onClick={handlePosAssist(
                  currentDevice,
                  deviceList?.findIndex(item => item.deviceId === currentServingOrder?.device?.deviceId)
                )}
              >
                <button className={cx('item-btn')}>
                  <Icon name="pos-assist-2" className={cx['icon']} />
                </button>
                <div>定位辅助</div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={cx('device-lists-wrapper')}>
          <Carousel>
            {deviceCardList?.map((item, key) => (
              <div className={cx['device-list']} key={key}>
                {item.map((item, index) => {
                  const originalDeviceState =
                    item?.state === 'NotUse'
                      ? deviceListOriginal?.find(itm => itm.deviceId === item.deviceId)?.state
                      : item.state;
                  return (
                    <div
                      className={cx('device-item', isShowMachineLockState(item) ? 'Idle' : `${item.state}`)}
                      key={item.deviceId}
                      onClick={item?.state === 'NotUse' ? handleDeviceItemNotUse(item) : handleClickDeviceItem(item)}
                    >
                      <div className={cx('left')}>
                        <div className={cx('info')}>
                          <div className={cx('info-name')}>{item.deviceName}</div>
                          <div className={cx('info-id')}>
                            <div>{item.deviceId}</div>
                            {!!item?.softwareVersion && (
                              <div className={cx('device-version')}>V{item?.softwareVersion}</div>
                            )}
                          </div>
                          <div className={cx('info-box')}>
                            <div className={cx('info-state')}>
                              {isShowMachineLockState(item) ? '已锁机' : MachineState[originalDeviceState]}
                            </div>
                            {['Idle', 'ManualOnly'].includes(originalDeviceState) && !!item?.upgradeSoftwareVersion && (
                              <div className={cx('ota-upgrade-button')} onClick={e => handleUpgrade(item, e)}>
                                <span className={cx('info-text')}>开始升级</span>
                                <Icon name="start" className={cx('info-icon')} />
                              </div>
                            )}
                            {['Idle', 'ManualOnly'].includes(originalDeviceState) &&
                              !!item?.troubleShootToolsEnabled && (
                                <div className={cx('issue-detect-tool')} onClick={handleIssueDetect(item)}>
                                  <span className={cx('info-text')}>误差排查</span>
                                  <Icon name="start" className={cx('info-icon')} />
                                </div>
                              )}
                          </div>
                          {originalDeviceState === 'ManualOnly' && !isShowMachineLockState(item) && (
                            <div className={cx('recover-tips')}>
                              <Icon className={cx('icon')} name="info-circle" />
                              <span>{item?.errorMessage}</span>
                            </div>
                          )}
                        </div>
                        <div className={cx('option-btn')}>
                          {originalDeviceState === 'Abnormal' ? (
                            <div className={cx('abnormal-box')}>
                              <Icon className={cx('icon')} name="warning-filled" color="#ff0000" />
                              <span>{item?.errorMessage || '请松开急停按钮'}</span>
                            </div>
                          ) : originalDeviceState === 'UpgradeFail' ? (
                            <div className={cx('upgrade-box')}>
                              <div>本次升级时间过长</div>
                              <div className={cx('upgrade-box-gap')}>
                                请<span className={cx('reset-device-text')}>重启机器</span>并联系交付工程师处理
                              </div>
                            </div>
                          ) : originalDeviceState === 'UpgradeSuccess' ? (
                            <div className={cx('upgrade-box')}>
                              <div>本设备已升级完成</div>
                              <div className={cx('upgrade-box-gap')}>
                                请<span className={cx('reset-device-text')}>重启机器</span>以体验最新功能
                              </div>
                            </div>
                          ) : originalDeviceState === 'ShuttingDown' && !!item.shuttingDownTimeLeft ? (
                            <div className={cx('shutdown-box-wrapper')}>
                              <div className={cx('shutdown-box')}>
                                <Loading3QuartersOutlined spin className={cx('spin-icon')} />
                                <span>预计{calculationRemainTime(item.shuttingDownTimeLeft)}后设备将关闭</span>
                              </div>
                            </div>
                          ) : (
                            <>
                              {!isNewSoftwareVersion(item?.softwareVersion, '2.0.0') && (
                                <>
                                  <div
                                    className={cx('control-item', { disabled: item.chassisState !== 'Static' })}
                                    onClick={handleParking(index, item)}
                                  >
                                    <button className={cx('item-btn')}>
                                      {item.chassisState === 'Locking' ? (
                                        <Loading3QuartersOutlined spin />
                                      ) : (
                                        <Icon name="lock" className={cx('icon')} />
                                      )}
                                    </button>
                                    <div>停驻{item.chassisState === 'Locking' ? '中' : ''}</div>
                                  </div>
                                  <div
                                    className={cx('control-item', { disabled: item?.chassisState !== 'Static' })}
                                    onClick={handleCancelParking(index, item)}
                                  >
                                    <button className={cx('item-btn')}>
                                      {item.chassisState === 'Unlocking' ? (
                                        <Loading3QuartersOutlined spin />
                                      ) : (
                                        <Icon name="unlock" className={cx('icon')} />
                                      )}
                                    </button>
                                    <div>解锁{item.chassisState === 'Unlocking' ? '中' : ''}</div>
                                  </div>
                                </>
                              )}

                              {item?.controlState !== 'lock' && (
                                <>
                                  <>
                                    {isNewSoftwareVersion(item.softwareVersion, '1.6.5') && (
                                      <div
                                        className={cx('control-item', 'is-show', {
                                          disabled: item.chassisState !== 'Static',
                                        })}
                                        onClick={e => {
                                          if (item?.state === 'NotUse') {
                                            handleDeviceItemNotUse(item);
                                            return;
                                          }
                                          e?.stopPropagation();
                                          handleSetMicState(item);
                                        }}
                                      >
                                        <button className={cx('item-btn')}>
                                          <Icon
                                            name={item?.microphoneState ? 'mic-close' : 'microphone'}
                                            className={cx('icon')}
                                          />
                                        </button>
                                        <div>{item?.microphoneState ? '闭麦' : '开麦'}</div>
                                      </div>
                                    )}
                                  </>
                                  <div
                                    className={cx('control-item', 'auxiliary-position', {
                                      disabled: originalDeviceState !== 'ManualOnly' && item?.chassisState !== 'Static',
                                    })}
                                    onClick={handlePosAssist(item, index)}
                                  >
                                    <button className={cx('item-btn')}>
                                      <Icon name="pos-assist-2" className={cx['icon']} />
                                    </button>
                                    <div>定位辅助</div>
                                  </div>
                                </>
                              )}
                              {/* 设备1.11.0版本，有关机操作 */}
                              {['ManualOnly', 'Idle']?.includes(item?.state) && item.softwareVersion === '1.11.0' && (
                                <div
                                  className={cx('control-item', {
                                    disabled: item.chassisState !== 'Static',
                                  })}
                                  onClick={handleShutdown(item, index)}
                                >
                                  <button className={cx('item-btn')}>
                                    <Icon name="shutting-down" className={cx['icon']} />
                                  </button>
                                  <div>关机</div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div className={cx('right')}>
                        <img
                          src={DeviceStatePhoto[originalDeviceState === 'Busy' ? 'online' : 'offline']}
                          alt="device"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </Carousel>
        </div>
      )}
    </div>
  );
};

export default DeviceManagement;
